import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { Modal, Image, Tooltip, Button } from 'antd'
import {
  PlayCircleFilled,
  EyeOutlined,
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateRightOutlined,
  CloseOutlined,
  CameraOutlined
} from '@ant-design/icons'

/**
 * props:
 * attachments: Array<{ src: string, type: 'image'|'video', name?: string, retakeFlag?: boolean, retakeId?: number }>
 * maxShow?: number (默认 6)
 */
export default function AppendixThumbs({ attachments = [], maxShow = 6 }) {
  const [gridOpen, setGridOpen] = useState(false)
  const [imageViewer, setImageViewer] = useState({ open: false, index: 0 })
  const [videoViewer, setVideoViewer] = useState({ open: false, src: '' })

  const data = useMemo(() => (Array.isArray(attachments) ? attachments.filter(a => a && a.src) : []), [attachments])

  const { showList, overflow } = useMemo(() => {
    const showList = data.slice(0, maxShow)
    const overflow = data.length - showList.length
    return { showList, overflow }
  }, [data, maxShow])

  const openViewer = useCallback(
    item => {
      if (item.type === 'video') {
        setVideoViewer({ open: true, src: item.src })
      } else {
        const onlyImages = data.filter(d => d.type === 'image')
        const idx = onlyImages.findIndex(d => d.src === item.src)
        setImageViewer({ open: true, index: Math.max(0, idx) })
      }
    },
    [data]
  )

  const downloadFile = useCallback(item => {
    const a = document.createElement('a')
    const paramsToRemove = ['type']
    const cleanedUrl = removeParamsFromUrl(item.src, paramsToRemove)

    a.href = cleanedUrl
    a.download =
      item.name ||
      item.src
        .split('?')[0]
        .split('/')
        .pop() ||
      (item.type === 'image' ? 'image' : 'video')
    a.target = '_blank'
    a.rel = 'noopener'
    a.click()
  }, [])

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Image.PreviewGroup>
        {showList.map((item, idx) => {
          const key = `${item.type}-${item.src}-${idx}`
          const commonBoxStyle = {
            width: 48,
            height: 48,
            borderRadius: 4,
            overflow: 'hidden',
            border: '1px solid #e5e5e5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }

          if (item.type === 'image') {
            return (
              <div key={key} style={{ position: 'relative' }}>
                <Image
                  src={item.src}
                  width={48}
                  height={48}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  preview={false}
                  fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'/>"
                  onClick={() => openViewer(item)}
                />

                {/* ✅ 补拍角标 */}
                {item.retakeFlag ? (
                  <div style={retakeBadgeStyle} title="补拍">
                    <CameraOutlined style={{ color: '#fff', fontSize: 12 }} />
                  </div>
                ) : null}

                <div style={actionBarStyle}>
                  <Tooltip title="查看">
                    <EyeOutlined onClick={() => openViewer(item)} style={actionIconStyle} />
                  </Tooltip>
                  <Tooltip title="下载">
                    <DownloadOutlined onClick={() => downloadFile(item)} style={actionIconStyle} />
                  </Tooltip>
                </div>
              </div>
            )
          }

          // 视频缩略图
          return (
            <div key={key} style={commonBoxStyle} title={item.src}>
              <PlayCircleFilled
                style={{ fontSize: 24, color: '#fa8c16', cursor: 'pointer' }}
                onClick={() => openViewer(item)}
              />
              <div style={actionBarStyle}>
                <Tooltip title="查看">
                  <EyeOutlined onClick={() => openViewer(item)} style={actionIconStyle} />
                </Tooltip>
                <Tooltip title="下载">
                  <DownloadOutlined onClick={() => downloadFile(item)} style={actionIconStyle} />
                </Tooltip>
              </div>
            </div>
          )
        })}
      </Image.PreviewGroup>

      {/* +x 入口 */}
      {overflow > 0 && (
        <Tooltip title={`还有 ${overflow} 个附件，点击查看全部`}>
          <span
            role="button"
            onClick={() => setGridOpen(true)}
            style={{ color: '#1677ff', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
          >
            +{overflow}
          </span>
        </Tooltip>
      )}

      {/* 全量附件网格页 */}
      <AttachmentGridModal
        open={gridOpen}
        attachments={data}
        onClose={() => setGridOpen(false)}
        onPreview={item => openViewer(item)}
        onDownload={item => downloadFile(item)}
      />

      {/* 图片预览 Modal */}
      <ImageViewerModal
        open={imageViewer.open}
        attachments={data.filter(d => d.type === 'image')}
        index={imageViewer.index}
        onClose={() => setImageViewer({ open: false, index: 0 })}
        onDownload={(src, name) => downloadFile({ src, type: 'image', name })}
      />

      {/* 视频播放 Modal */}
      <MyVideoViewerModal
        open={videoViewer.open}
        src={videoViewer.src}
        onClose={() => setVideoViewer({ open: false, src: '' })}
        onDownload={() => downloadFile({ src: videoViewer.src, type: 'video' })}
      />
    </div>
  )
}

/* ---------- 附件网格 Modal（10 列） ---------- */

function AttachmentGridModal({ open, attachments, onClose, onPreview, onDownload }) {
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 64px)',
    gap: 12
  }

  return (
    <Modal
      title="附件展示"
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      bodyStyle={{ maxHeight: 600, overflow: 'auto' }}
      zIndex={1000}
    >
      <div style={gridStyle}>
        {attachments.map((item, i) => {
          const key = `${item.type}-${item.src}-${i}`
          const box = {
            width: 64,
            height: 64,
            borderRadius: 6,
            overflow: 'hidden',
            border: '1px solid #e5e5e5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }

          if (item.type === 'image') {
            return (
              <div key={key} style={{ position: 'relative', ...box }}>
                <img
                  src={item.src}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => onPreview(item)}
                  alt=""
                />

                {/* ✅ 补拍角标 */}
                {item.retakeFlag ? (
                  <div style={{ ...retakeBadgeStyle, width: 20, height: 20 }} title="补拍">
                    <CameraOutlined style={{ color: '#fff', fontSize: 12 }} />
                  </div>
                ) : null}

                <div style={actionBarStyle}>
                  <EyeOutlined onClick={() => onPreview(item)} style={actionIconStyle} />
                  <DownloadOutlined onClick={() => onDownload(item)} style={actionIconStyle} />
                </div>
              </div>
            )
          }
          return (
            <div key={key} style={box} title={item.src}>
              <PlayCircleFilled
                style={{ fontSize: 28, color: '#fa8c16', cursor: 'pointer' }}
                onClick={() => onPreview(item)}
              />
              <div style={actionBarStyle}>
                <EyeOutlined onClick={() => onPreview(item)} style={actionIconStyle} />
                <DownloadOutlined onClick={() => onDownload(item)} style={actionIconStyle} />
              </div>
            </div>
          )
        })}
      </div>
    </Modal>
  )
}

/* ---------- 图片查看 Modal（上一/下一、缩放、旋转、下载） ---------- */

function ImageViewerModal({ open, attachments, index, onClose, onDownload }) {
  const [cur, setCur] = useState(index)
  const [scale, setScale] = useState(1)
  const [rotate, setRotate] = useState(0)

  useEffect(() => {
    if (open) {
      setCur(index)
      setScale(1)
      setRotate(0)
    }
  }, [open, index])

  const hasPrev = cur > 0
  const hasNext = cur < attachments.length - 1
  const current = attachments[cur]

  const toolbarStyle = {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12
  }

  return (
    <Modal
      title="图片预览"
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      bodyStyle={{ textAlign: 'center' }}
      zIndex={2000}
      getContainer={document.body}
    >
      {current ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <Button icon={<LeftOutlined />} disabled={!hasPrev} onClick={() => setCur(c => Math.max(0, c - 1))} />

            <div
              style={{
                width: 700,
                height: 500,
                overflow: 'hidden',
                border: '1px solid #eee',
                borderRadius: 8,
                position: 'relative'
              }}
            >
              <img
                src={current.src}
                alt=""
                style={{
                  transform: `scale(${scale}) rotate(${rotate}deg)`,
                  transformOrigin: 'center center',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />

              {/* ✅ 详情页补拍角标 */}
              {current.retakeFlag ? (
                <div
                  style={{
                    ...retakeBadgeStyle,
                    top: 12,
                    right: 12,
                    width: 28,
                    height: 28,
                    borderRadius: 6
                  }}
                  title="补拍"
                >
                  <CameraOutlined style={{ color: '#fff', fontSize: 16 }} />
                </div>
              ) : null}
            </div>

            <Button
              icon={<RightOutlined />}
              disabled={!hasNext}
              onClick={() => setCur(c => Math.min(attachments.length - 1, c + 1))}
            />
          </div>

          <div style={toolbarStyle}>
            <Button icon={<ZoomInOutlined />} onClick={() => setScale(s => Math.min(5, s + 0.2))}>
              放大
            </Button>
            <Button icon={<ZoomOutOutlined />} onClick={() => setScale(s => Math.max(0.2, s - 0.2))}>
              缩小
            </Button>
            <Button icon={<RotateRightOutlined />} onClick={() => setRotate(r => (r + 90) % 360)}>
              旋转 90°
            </Button>
            <Button icon={<DownloadOutlined />} type="primary" onClick={() => onDownload(current.src, current.name)}>
              下载图片
            </Button>
            <Button icon={<CloseOutlined />} onClick={onClose}>
              关闭
            </Button>
          </div>
        </>
      ) : (
        <div style={{ padding: 40, color: '#999' }}>无图片</div>
      )}
    </Modal>
  )
}

/* ---------- 视频播放 Modal ---------- */

function MyVideoViewerModal({ open, src, onClose }) {
  const handleDownload = () => {
    const paramsToRemove = ['type']
    const cleanedUrl = removeParamsFromUrl(src, paramsToRemove)
    triggerDownload(cleanedUrl)
  }
  return (
    <Modal
      title="视频预览"
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      bodyStyle={{ textAlign: 'center' }}
      zIndex={2000}
      getContainer={document.body}
    >
      {src ? (
        <>
          <video
            src={src}
            controls
            style={{ width: '100%', maxHeight: 520, background: '#000' }}
            autoPlay
            preload="auto"
          />
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 8 }}>
            <Button icon={<DownloadOutlined />} type="primary" onClick={handleDownload}>
              下载视频
            </Button>
            <Button icon={<CloseOutlined />} onClick={onClose}>
              关闭
            </Button>
          </div>
        </>
      ) : (
        <div style={{ padding: 40, color: '#999' }}>无视频</div>
      )}
    </Modal>
  )
}

/* ---------- util ---------- */

function removeParamsFromUrl(rawUrl, paramsToRemove = []) {
  try {
    const url = new URL(rawUrl, window.location.origin)
    paramsToRemove.forEach(p => url.searchParams.delete(p))
    return url.toString()
  } catch (e) {
    return rawUrl
  }
}

function triggerDownload(url, filename = '') {
  const a = document.createElement('a')
  a.href = url
  if (!filename) {
    try {
      const u = new URL(url, window.location.origin)
      const pathname = u.pathname
      const last = pathname.split('/').pop() || 'video'
      filename = last
    } catch {
      filename = 'video'
    }
  }
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/* ---------- 通用样式（悬浮操作条） ---------- */

const actionBarStyle = {
  position: 'absolute',
  right: 4,
  bottom: 4,
  display: 'flex',
  gap: 6,
  background: 'rgba(255,255,255,0.9)',
  borderRadius: 4,
  padding: '2px 4px',
  zIndex: 2
}

const actionIconStyle = {
  fontSize: 14,
  color: '#595959',
  cursor: 'pointer'
}

/* ✅ 补拍角标样式（缩略图/网格/详情复用） */
const retakeBadgeStyle = {
  position: 'absolute',
  top: 2,
  right: 2,
  width: 18,
  height: 18,
  borderRadius: 4,
  background: 'rgba(22, 119, 255, 0.92)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
  zIndex: 3
}
